import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { MaintCardShell } from './MaintCardShell';
import { ConfirmApplyDialog } from './ConfirmApplyDialog';

export default function DuplicateNumbersCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/maintenance/duplicate-numbers/scan');
      setData(res.data);
      setSelected({});
    } catch (e) { toast.error(e.response?.data?.detail || 'Scan failed'); }
    finally { setLoading(false); }
  };

  const toggleGroup = (idx, checked) => setSelected((s) => ({ ...s, [idx]: checked }));

  const buildMapping = () => {
    const mapping = {};
    (data?.duplicate_groups || []).forEach((g, idx) => {
      if (!selected[idx]) return;
      g.proposed_renumbering.forEach((p) => { if (p.proposed_number) mapping[p.id] = p.proposed_number; });
    });
    return mapping;
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const apply = async () => {
    setApplying(true);
    try {
      const mapping = buildMapping();
      const res = await api.post('/admin/maintenance/duplicate-numbers/apply', { mapping });
      toast.success(`${res.data.total_updated} document(s) renumbered`);
      setConfirmOpen(false);
      scan();
    } catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
    finally { setApplying(false); }
  };

  return (
    <MaintCardShell
      title="Duplicate numbers"
      description="Invoices, estimates, jobs or permits sharing the same visible number (e.g. 8 invoices as I-2026-2026). Proposes a renumbering, oldest keeps its number."
      testId="dup-numbers"
      count={data ? data.total_duplicate_numbers : null}
      loading={loading}
      onScan={scan}
    >
      {data && data.duplicate_groups.length > 0 && (
        <div className="space-y-3">
          {data.numbering_jump_warning?.length > 0 && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="dup-numbers-jump-warning">
              {data.numbering_jump_warning.map((w, i) => (
                <p key={i}>Next {w.collection} number would jump to {w.next_number_would_be} (configured start number is {w.jump_size} ahead of the real max).</p>
              ))}
            </div>
          )}
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {data.duplicate_groups.map((g, idx) => (
              <div key={idx} className="border border-gray-100 rounded-lg p-3 text-xs" data-testid={`dup-numbers-group-${idx}`}>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <Checkbox data-testid={`dup-numbers-group-${idx}-checkbox`} checked={!!selected[idx]} onCheckedChange={(v) => toggleGroup(idx, !!v)} />
                  <span className="font-semibold text-gray-800">{g.collection} · {g.number} · {g.count} documents</span>
                </label>
                <div className="pl-6 space-y-1">
                  <p className="text-gray-500">Keeps: {g.keeper.id.slice(0, 8)} ({g.keeper.total ?? '—'})</p>
                  {g.proposed_renumbering.map((p) => (
                    <p key={p.id} className="text-gray-600">{p.id.slice(0, 8)} → <span className="font-semibold text-emerald-700">{p.proposed_number || 'no proposal'}</span></p>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Button data-testid="dup-numbers-apply-btn" size="sm" disabled={selectedCount === 0} onClick={() => setConfirmOpen(true)} className="bg-red-600 hover:bg-red-700 text-white">
            Apply selected ({selectedCount})
          </Button>
        </div>
      )}
      {data && data.duplicate_groups.length === 0 && <p className="text-xs text-emerald-600">No duplicates found.</p>}

      <ConfirmApplyDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={apply}
        confirming={applying}
        title="Renumber duplicate documents"
        summary={`This will renumber the documents in ${selectedCount} selected group(s). Only the numbering field + activity log are touched — nothing else.`}
      />
    </MaintCardShell>
  );
}
